import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, Reply, Loader2, Search, Mail, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Outbox() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // 🆕 Elimina un'email inviata (hard delete: la voce sparisce dallo storico)
  const deleteEmail = async (e) => {
    if (deletingId) return;
    const ok = window.confirm(`Eliminare definitivamente questa email?\n\nOggetto: "${e.subject || "(senza oggetto)"}"\nA: ${(e.to || []).join(", ")}\n\n⚠ L'operazione è irreversibile.`);
    if (!ok) return;
    setDeletingId(e.id);
    try {
      await api.delete(`/outbox/${e.id}`);
      setList((prev) => prev.filter((x) => x.id !== e.id));
      toast.success("Email eliminata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione fallita");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/outbox");
        setList(data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = q.trim()
    ? list.filter((e) => {
        const needle = q.toLowerCase();
        const blob = [
          (e.to || []).join(" "),
          e.subject || "",
          (e.user_name || ""),
        ].join(" ").toLowerCase();
        return blob.includes(needle);
      })
    : list;

  return (
    <div className="space-y-5" data-testid="outbox-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Storico invii</p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Send className="h-6 w-6 text-emerald-600" /> Email inviate
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Log di tutte le email spedite dalla CRM (risposte + nuove email). Massimo ultime 200.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="outbox-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per destinatario, oggetto, mittente…"
            className="pl-8"
          />
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center text-slate-500">
            <Mail className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">{q ? "Nessun risultato per la ricerca corrente." : "Nessuna email inviata ancora."}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{filtered.length} email</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-slate-100">
              {filtered.map((e) => (
                <li key={e.id} className="p-4 hover:bg-slate-50 transition" data-testid={`outbox-row-${e.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {e.reply_to_email_id ? (
                          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                            <Reply className="h-3 w-3 mr-1" /> Risposta
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">
                            <Send className="h-3 w-3 mr-1" /> Nuova
                          </Badge>
                        )}
                        <span className="text-xs text-slate-500">{formatDate(e.sent_at)}</span>
                        {e.attachment_count > 0 && (
                          <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                            <Paperclip className="h-3 w-3" /> {e.attachment_count}
                          </span>
                        )}
                      </div>
                      <div className="font-medium text-sm mt-1 truncate">{e.subject || "(senza oggetto)"}</div>
                      <div className="text-xs text-slate-600 mt-0.5 truncate">
                        <b>A:</b> {(e.to || []).join(", ")}
                        {e.cc && e.cc.length > 0 && <span className="ml-2"><b>CC:</b> {e.cc.join(", ")}</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Inviata da {e.user_name || "—"}
                      </div>
                    </div>
                    {/* 🆕 Pulsante elimina */}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700 flex-shrink-0"
                      onClick={() => deleteEmail(e)}
                      disabled={deletingId === e.id}
                      title="Elimina email dallo storico"
                      data-testid={`outbox-delete-${e.id}`}
                    >
                      {deletingId === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
